import axios, { AxiosError } from 'axios';
import { checkNow, getConnectivity, isNetworkError, isOnline, reportNetworkFailure } from '../services/connectivity';
import { getAccessToken } from '../services/sharedDataService';
import { Auth } from 'aws-amplify';
import { getSelectedOrgId } from '../services/selectedOrg';
import { attachErrorReference } from '../services/errorReference';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api',
  headers: {
    'Content-Type': 'application/json'
  }
});

const WRITE_METHODS = new Set(['post', 'put', 'patch', 'delete']);

const OFFLINE_WRITE_MESSAGES = {
  offline: "You're offline — nothing was saved. Try again once you're reconnected.",
  unreachable: "Can't reach the server — nothing was saved. Try again once it's back.",
} as const;
const NO_RESPONSE_WRITE_MESSAGE = "We couldn't confirm this saved — check once you're back online.";
const NO_RESPONSE_READ_MESSAGE = "Couldn't reach the server. Check your connection and try again.";

/**
 * Connectivity failures carry their message where the app's error displays
 * already look (`err.response.data.error`), so every existing
 * `err?.response?.data?.error || 'Failed to …'` shows it without changes.
 * `status: 0` matches no real HTTP status a caller branches on, and
 * `isConnectivityError` lets anything that needs to tell them apart do so.
 */
const withConnectivityMessage = (error: any, message: string) => {
  error.isConnectivityError = true;
  error.response = { status: 0, data: { error: message, message }, headers: {}, config: error.config };
  return error;
};

// Add interceptor to inject the access token and selected organisation on every request
api.interceptors.request.use(
  async (config) => {
    // Refuse writes up front while we know the server can't be reached: an
    // honest answer now instead of a timeout later. Reads are let through —
    // they fail harmlessly and their failure is what tells us we're back.
    const method = (config.method || 'get').toLowerCase();
    if (WRITE_METHODS.has(method) && !isOnline()) {
      const { status } = getConnectivity();
      // "Unreachable" may be stale between recovery probes; give the server one
      // fresh look before refusing, so the first click after it returns works.
      const recovered = status === 'unreachable' ? await checkNow() : false;
      if (!recovered) {
        const lost = getConnectivity().status === 'offline' ? 'offline' : 'unreachable';
        throw withConnectivityMessage(new AxiosError(OFFLINE_WRITE_MESSAGES[lost], 'ERR_OFFLINE', config), OFFLINE_WRITE_MESSAGES[lost]);
      }
    }

    try {
      const accessToken = await getAccessToken();
      config.headers.Authorization = `Bearer ${accessToken}`;
    } catch (err) {
      console.warn('No access token available:', err);
    }

    // Inject THIS TAB's organisation so the backend scopes data correctly.
    // Per tab (see services/selectedOrg.ts) — a shared value let one tab's
    // organisation switch change what every other tab sent.
    const selectedOrgId = getSelectedOrgId();
    if (selectedOrgId) {
      config.headers['X-Organization-Id'] = selectedOrgId;
    }

    // Inject active user token (PIN-authenticated staff member).
    // Stored in sessionStorage so it clears when the browser session ends.
    const activeUserToken = sessionStorage.getItem('activeUserToken');
    if (activeUserToken) {
      config.headers['X-Active-User-Token'] = activeUserToken;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Guards against multiple concurrent 401s (e.g. the dashboard's 20s poll fires
// two requests at once) all racing to sign out / redirect.
let handlingAuthFailure = false;

// Exported so other involuntary-sign-out paths (e.g. the proactive session
// expiry guard) tear the session down identically: stash a one-off notice for
// the login screen, clear the PIN token, end the Cognito session, redirect.
export const signOutAndRedirect = async (reason?: string) => {
  if (reason) sessionStorage.setItem('authMessage', reason);
  sessionStorage.removeItem('activeUserToken');
  sessionStorage.removeItem('deviceLocked');
  await Auth.signOut();
  window.location.href = '/login';
};

api.interceptors.response.use(
  response => response,
  async error => {
    // Refused up front by the request interceptor — nothing more to do.
    if (error.isConnectivityError) return Promise.reject(error);

    // No response at all (and not a deliberate cancel): the network, or the
    // server, didn't answer. Let connectivity probe to find out which, and
    // give the caller a message that says what happened. (No server reference
    // to attach — the server never saw it.)
    if (!error.response && !axios.isCancel(error)) {
      reportNetworkFailure();
      const method = (error.config?.method || 'get').toLowerCase();
      return Promise.reject(withConnectivityMessage(
        error,
        WRITE_METHODS.has(method) ? NO_RESPONSE_WRITE_MESSAGE : NO_RESPONSE_READ_MESSAGE,
      ));
    }

    // A server-side failure gets its reference appended to the message the
    // screen shows ("… (Ref: <id>)"), so a user can quote it and System
    // Admin → Errors can find it. 5xx only — see services/errorReference.ts.
    attachErrorReference(error);

    const status = error.response?.status;
    const path = window.location.pathname;

    if (status === 401 && !path.startsWith('/login') && !handlingAuthFailure) {
      handlingAuthFailure = true;
      try {
        // The API only ever returns 401 in two situations (see tdp-api
        // authMiddleware.ts): the Cognito credential failed, or the PIN was
        // reset server-side (PIN_RESET_REQUIRED). An *expired* PIN token is
        // silently ignored by the API and never 401s — so there is no
        // "re-enter PIN" case to handle here.
        if (error.response?.data?.error === 'PIN_RESET_REQUIRED') {
          // PIN was wiped (admin reset / "Forget PIN"). The user can't re-enter
          // the old PIN — they must sign back in and set up a new one.
          await signOutAndRedirect(
            error.response.data.message || 'Your PIN was reset. Please sign in again to set up a new PIN.'
          );
        } else {
          // Every other 401 is a Cognito failure — but confirm the session is
          // genuinely dead before tearing it down. currentSession() auto-
          // refreshes; if it succeeds, this was a transient/unexpected 401
          // (e.g. a backend blip) and we must NOT log the user out — the next
          // poll recovers. Only sign out when the session truly can't refresh.
          try {
            await Auth.currentSession();
            // Session is healthy — swallow the redirect; let the error
            // propagate so the caller (e.g. silent poll) can retry.
          } catch (refreshErr) {
            // A refresh that failed for want of a network says nothing about
            // the session — the connection dropped between the 401 and the
            // check. Leave the user signed in; the next call settles it.
            if (isNetworkError(refreshErr)) {
              reportNetworkFailure();
            } else {
              await signOutAndRedirect();
            }
          }
        }
      } catch {
        // Defensive: never let the handler itself swallow the rejection.
        await signOutAndRedirect();
      } finally {
        handlingAuthFailure = false;
      }
    }
    return Promise.reject(error);
  }
);

export default api;
